export class AysVirtualClinicAnnualAccountParts {

  constructor(
      public aktiva?: AysVirtualClinicAnnualAccountAktiva,
      public passiva?: AysVirtualClinicAnnualAccountPassiva,
      public guv?: AysVirtualClinicAnnualAccountGuV
  ) {
  }

  public static fromObj(obj: any): AysVirtualClinicAnnualAccountParts {
    return new AysVirtualClinicAnnualAccountParts(
        AysVirtualClinicAnnualAccountAktiva.fromObj(obj.aktiva),
        AysVirtualClinicAnnualAccountPassiva.fromObj(obj.passiva),
        AysVirtualClinicAnnualAccountGuV.fromObj(obj.guv)
    );
  }
}

/**
 * Reads a captured Jahresabschluss into the flat `key -> value` map the shared
 * statement view is built from.
 *
 * The capture schema is far wider than the clinic browser's read model — around
 * 160 fields across the three parts, down to `abii3aa`. That read model
 * publishes a ratified 24-position subset of exactly those field names, so
 * pulling the subset out is the whole adapter: a Virtual Clinic and a real
 * clinic then render the identical statement, from the identical positions.
 *
 * Fields the capture form left blank arrive as `undefined` and are normalised
 * to `null` — "not reported", which the view must keep apart from a filed `0`.
 */
export function aysVirtualClinicAnnualAccountValues(
    parts: AysVirtualClinicAnnualAccountParts | undefined,
    keys: readonly string[]
): Record<string, number | null> {
  const flat: Record<string, unknown> = {
    ...(parts?.aktiva ?? {}),
    ...(parts?.passiva ?? {}),
    ...(parts?.guv ?? {})
  };

  return Object.fromEntries(
      keys.map((key) => {
        const value = flat[key];
        return [key, typeof value === 'number' ? value : null];
      })
  );
}

export class AysVirtualClinicAnnualAccountAktiva {

  constructor(
      public aa?: number,
      public aai?: number,
      public aai1?: number,
      public aai2?: number,
      public aai3?: number,
      public aai4?: number,
      public aaii?: number,
      public aaii1?: number,
      public aaii2?: number,
      public aaii3?: number,
      public aaii4?: number,
      public aaii5?: number,
      public aaii6?: number,
      public aaii7?: number,
      public aaii8?: number,
      public aaiii?: number,
      public aaiii1?: number,
      public aaiii2?: number,
      public aaiii3?: number,
      public aaiii4?: number,
      public aaiii5?: number,
      public aaiii6?: number,
      public aaiii7?: number,
      public ab?: number,
      public abi?: number,
      public abi1?: number,
      public abi2?: number,
      public abi3?: number,
      public abi4?: number,
      public abii?: number,
      public abii1?: number,
      public abii2?: number,
      public abii2a?: number,
      public abii3?: number,
      public abii3a?: number,
      public abii3aa?: number,
      public abii4?: number,
      public abii5?: number,
      public abii6?: number,
      public abii6a?: number,
      public abiii?: number,
      public abiii1?: number,
      public abiv?: number,
      public ac?: number,
      public ac1?: number,
      public ac2?: number,
      public ad?: number,
      public ad1?: number,
      public ad2?: number,
      public ae?: number,
      public af?: number,
      public ag?: number,
      public summe_aktiva?: number
  ) {

  }

  public static fromObj(obj: any): AysVirtualClinicAnnualAccountAktiva {
    return new AysVirtualClinicAnnualAccountAktiva(
        obj.aa,
        obj.aai,
        obj.aai1,
        obj.aai2,
        obj.aai3,
        obj.aai4,
        obj.aaii,
        obj.aaii1,
        obj.aaii2,
        obj.aaii3,
        obj.aaii4,
        obj.aaii5,
        obj.aaii6,
        obj.aaii7,
        obj.aaii8,
        obj.aaiii,
        obj.aaiii1,
        obj.aaiii2,
        obj.aaiii3,
        obj.aaiii4,
        obj.aaiii5,
        obj.aaiii6,
        obj.aaiii7,
        obj.ab,
        obj.abi,
        obj.abi1,
        obj.abi2,
        obj.abi3,
        obj.abi4,
        obj.abii,
        obj.abii1,
        obj.abii2,
        obj.abii2a,
        obj.abii3,
        obj.abii3a,
        obj.abii3aa,
        obj.abii4,
        obj.abii5,
        obj.abii6,
        obj.abii6a,
        obj.abiii,
        obj.abiii1,
        obj.abiv,
        obj.ac,
        obj.ac1,
        obj.ac2,
        obj.ad,
        obj.ad1,
        obj.ad2,
        obj.ae,
        obj.af,
        obj.ag,
        obj.summe_aktiva
    );
  }
}

export class AysVirtualClinicAnnualAccountPassiva {

  constructor(
      public pa?: number,
      public pai?: number,
      public paii?: number,
      public paiii?: number,
      public paiii1?: number,
      public paiii2?: number,
      public paiii3?: number,
      public paiv?: number,
      public pav?: number,
      public pavi?: number,
      public pavii?: number,
      public paviii?: number,
      public paix?: number,
      public pax?: number,
      public paxi?: number,
      public paxii?: number,
      public paxiii?: number,
      public pb?: number,
      public pb1?: number,
      public pb2?: number,
      public pb3?: number,
      public pb4?: number,
      public pc?: number,
      public pc1?: number,
      public pc2?: number,
      public pc3?: number,
      public pd?: number,
      public pd1?: number,
      public pd1a?: number,
      public pd1b?: number,
      public pd2?: number,
      public pd3?: number,
      public pd3a?: number,
      public pd4?: number,
      public pd4a?: number,
      public pd5?: number,
      public pd5a?: number,
      public pd5b?: number,
      public pd6?: number,
      public pd6a?: number,
      public pd7?: number,
      public pd7a?: number,
      public pd8?: number,
      public pd8a?: number,
      public pd9?: number,
      public pd9a?: number,
      public pd10?: number,
      public pd10a?: number,
      public pd10b?: number,
      public pd10c?: number,
      public pe?: number,
      public pf?: number,
      public pg?: number,
      public summe_passiva?: number
  ) {
  }

  public static fromObj(obj: any): AysVirtualClinicAnnualAccountPassiva {
    return new AysVirtualClinicAnnualAccountPassiva(
        obj.pa,
        obj.pai,
        obj.paii,
        obj.paiii,
        obj.paiii1,
        obj.paiii2,
        obj.paiii3,
        obj.paiv,
        obj.pav,
        obj.pavi,
        obj.pavii,
        obj.paviii,
        obj.paix,
        obj.pax,
        obj.paxi,
        obj.paxii,
        obj.paxiii,
        obj.pb,
        obj.pb1,
        obj.pb2,
        obj.pb3,
        obj.pb4,
        obj.pc,
        obj.pc1,
        obj.pc2,
        obj.pc3,
        obj.pd,
        obj.pd1,
        obj.pd1a,
        obj.pd1b,
        obj.pd2,
        obj.pd3,
        obj.pd3a,
        obj.pd4,
        obj.pd4a,
        obj.pd5,
        obj.pd5a,
        obj.pd5b,
        obj.pd6,
        obj.pd6a,
        obj.pd7,
        obj.pd7a,
        obj.pd8,
        obj.pd8a,
        obj.pd9,
        obj.pd9a,
        obj.pd10,
        obj.pd10a,
        obj.pd10b,
        obj.pd10c,
        obj.pe,
        obj.pf,
        obj.pg,
        obj.summe_passiva
    );
  }
}

export class AysVirtualClinicAnnualAccountGuV {

  constructor(
      public g0?: number,
      public g1?: number,
      public g2?: number,
      public g3?: number,
      public g3a?: number,
      public g3b?: number,
      public g3c?: number,
      public g3d?: number,
      public g4?: number,
      public g5?: number,
      public g6?: number,
      public g7?: number,
      public g7a?: number,
      public g8?: number,
      public g9?: number,
      public g9a?: number,
      public g9b?: number,
      public g9ba?: number,
      public g11?: number,
      public g11a?: number,
      public g11b?: number,
      public g12?: number,
      public g12a?: number,
      public g13?: number,
      public g14?: number,
      public g15?: number,
      public g16?: number,
      public g16a?: number,
      public g16aa?: number,
      public g16b?: number,
      public g16c?: number,
      public g16d?: number,
      public g16e?: number,
      public g16f?: number,
      public g16g?: number,
      public g17?: number,
      public g18?: number,
      public g18a?: number,
      public g18b?: number,
      public g18c?: number,
      public g18d?: number,
      public g18e?: number,
      public g18ea?: number,
      public g18eb?: number,
      public g18f?: number,
      public g19?: number,
      public g20?: number,
      public g21?: number,
      public g22?: number,
      public g23?: number,
      public g23a?: number,
      public g24?: number,
      public g25?: number,
      public g26?: number
  ) {
  }

  public static fromObj(obj: any): AysVirtualClinicAnnualAccountGuV {
    return new AysVirtualClinicAnnualAccountGuV(
        obj.g0,
        obj.g1,
        obj.g2,
        obj.g3,
        obj.g3a,
        obj.g3b,
        obj.g3c,
        obj.g3d,
        obj.g4,
        obj.g5,
        obj.g6,
        obj.g7,
        obj.g7a,
        obj.g8,
        obj.g9,
        obj.g9a,
        obj.g9b,
        obj.g9ba,
        obj.g11,
        obj.g11a,
        obj.g11b,
        obj.g12,
        obj.g12a,
        obj.g13,
        obj.g14,
        obj.g15,
        obj.g16,
        obj.g16a,
        obj.g16aa,
        obj.g16b,
        obj.g16c,
        obj.g16d,
        obj.g16e,
        obj.g16f,
        obj.g16g,
        obj.g17,
        obj.g18,
        obj.g18a,
        obj.g18b,
        obj.g18c,
        obj.g18d,
        obj.g18e,
        obj.g18ea,
        obj.g18eb,
        obj.g18f,
        obj.g19,
        obj.g20,
        obj.g21,
        obj.g22,
        obj.g23,
        obj.g23a,
        obj.g24,
        obj.g25,
        obj.g26
    );
  }
}
